import { useCallback, useMemo } from "react";
import {
  Box,
  DEFAULT_DATE_PRESETS,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Menu,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
  type EntitySelectorConfig,
  type OnChangeFn,
  type Omittable,
  type SortingState,
} from "@salestrak/ui";

import { EntityGroupTag } from "#app/components/entity";
import { usePoolSelection } from "#app/components/entity-selectors/use-pool-selection";
import { FilterValueChip } from "#app/components/filters/filter-value-chip";
import {
  EntityPickChip,
  useMemberChipValue,
} from "#app/components/members/member-quick-filters";
import type { EntityData } from "#app/lib/entity-visuals";
import { useMemberEntityResolver } from "#app/lib/data/member-directory-provider";
import type { UseTableFiltersResult } from "#app/lib/hooks/use-table-filters";
import { SortPresetChip, type SortPreset } from "#app/components/filters/sort-preset-chip";

// ---------------------------------------------------------------------------
// The All Teams directory's quick-filter chips — the members directory's chip
// grammar (`FilterValueChip` "Label: Value ⌄") over the teams query's facets:
// Members (roster contains), Created (creation-date window), Creator, and the
// Sort presets chip. The chips write the SAME `useTableFilters` state the
// funnel defs map to a where, so the settings page's funnel and this band can
// never disagree about what a filter means.
// ---------------------------------------------------------------------------

/** Def keys — each must match a def in `buildTeamsFilterDefs`, which owns the
 *  values → where mapping. */
const TEAM_FILTER_KEYS = {
  // rosterIds: "Members" means on the team in any role — see the def.
  members: "rosterIds",
  creator: "createdByIds",
  created: "creationDate",
} as const;

type ChipsProps = Omittable<{
  filters: UseTableFiltersResult<EntityItemConfig>;
  /** The workspace member pool (`useUserPool`): a window of pages plus server
   *  search, so a chip finds every member, not only the first page. */
  memberPool: EntityPoolConfig;
}>;


export function TeamQuickFilters({ filters, memberPool }: ChipsProps) {
  const { filterValues, setFilterValues } = filters;

  return (
    <>
      <TeamMembersChip
        memberPool={memberPool}
        value={filterValues[TEAM_FILTER_KEYS.members] ?? []}
        onChange={(ids) => setFilterValues(TEAM_FILTER_KEYS.members, ids)}
      />
      <TeamCreatedChip
        value={filterValues[TEAM_FILTER_KEYS.created]?.[0] ?? null}
        onChange={(value) =>
          setFilterValues(TEAM_FILTER_KEYS.created, value == null ? [] : [value])
        }
      />
      <TeamCreatorChip
        memberPool={memberPool}
        value={filterValues[TEAM_FILTER_KEYS.creator]?.[0] ?? null}
        onChange={(id) =>
          setFilterValues(TEAM_FILTER_KEYS.creator, id == null ? [] : [id])
        }
      />
    </>
  );
}

// --- Members: teams whose roster contains ALL picked members ---------------

function TeamMembersChip({
  memberPool,
  value,
  onChange,
}: Omittable<{
  memberPool: EntityPoolConfig;
  value: string[];
  onChange: (ids: string[]) => void;
}>) {
  const resolveMember = useMemberEntityResolver();

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [memberPool],
      searchPlaceholder: "Search members...",
      emptyMessage: "No members found",
    }),
    [memberPool],
  );

  const { selections, toIds } = usePoolSelection(config.pools, value);

  const handleChange = useCallback(
    (next: EntitySelection[]) => onChange(toIds(next)),
    [onChange, toIds],
  );

  // The picked members as a tiny avatar stack ("+N" past three) — the chip's
  // press owns the interaction, so the stack is inert.
  const chipValue = useMemo(() => {
    if (value.length === 0) return undefined;
    const entities = value
      .map((id) => resolveMember(id))
      .filter((entity): entity is EntityData => !!entity);
    if (!entities.length) return <Box as="span">{String(value.length)}</Box>;
    return (
      // The 16px chip-value rung — the same avatar size the sibling chips'
      // single values render (VALUE_VISUAL_SIZE), inside the 24px chip.
      <EntityGroupTag
        entities={entities}
        size="2xs"
        max={3}
        interactive={false}
        hoverDisabled
      />
    );
  }, [value, resolveMember]);

  return (
    <EntitySelector config={config} value={selections} onValueChange={handleChange} title="Members">
      <EntitySelectorTrigger
        render={
          <FilterValueChip
            label="Members"
            value={chipValue}
            onClear={() => onChange([])}
            clearLabel="Clear members"
          />
        }
      />
      <EntitySelectorContent>
        <EntitySelectorInput />
        <EntitySelectorPool value={memberPool.id} />
        <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}

// --- Created: creation-date window ------------------------------------------

function TeamCreatedChip({
  value,
  onChange,
}: Omittable<{
  value: string | null;
  onChange: (value: string | null) => void;
}>) {
  const label = value
    ? DEFAULT_DATE_PRESETS.find((p) => p.value === value)?.label
    : undefined;
  return (
    <Menu>
      <MenuTrigger
        render={
          <FilterValueChip
            label="Created"
            value={label}
            onClear={() => onChange(null)}
            clearLabel="Clear created"
          />
        }
      />
      <MenuPopup align="start" width="select">
        <MenuRadioGroup
          value={value ?? ""}
          onValueChange={(next) => onChange(next || null)}
        >
          {DEFAULT_DATE_PRESETS.map((preset) => (
            <MenuRadioItem key={preset.value} value={preset.value}>
              {preset.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
}

// --- Creator: who made the team ---------------------------------------------

function TeamCreatorChip({
  memberPool,
  value,
  onChange,
}: Omittable<{
  memberPool: EntityPoolConfig;
  value: string | null;
  onChange: (id: string | null) => void;
}>) {
  const renderValue = useMemberChipValue();
  return (
    <EntityPickChip
      label="Creator"
      clearLabel="Clear creator"
      pool={memberPool}
      searchPlaceholder="Search members..."
      emptyMessage="No members found"
      value={value}
      onChange={onChange}
      renderValue={renderValue}
    />
  );
}

// --- Sort --------------------------------------------------------------------

type TeamSortKey = "default" | "az" | "za" | "mostMembers" | "leastMembers";

// "Default" is the server's own order (team name), the EMPTY sort — the same
// baseline rule the members Sort chip follows. The member-count orderings map
// to the server's computed `memberCount` key; no column header writes them,
// so the chip is their only author.
const TEAM_SORT_PRESETS: SortPreset<TeamSortKey>[] = [
  { key: "default", label: "Default", sorting: [] },
  { key: "az", label: "A – Z", sorting: [{ id: "teamName", desc: false }] },
  { key: "za", label: "Z – A", sorting: [{ id: "teamName", desc: true }] },
  { key: "mostMembers", label: "Most Members", sorting: [{ id: "memberCount", desc: true }] },
  { key: "leastMembers", label: "Least Members", sorting: [{ id: "memberCount", desc: false }] },
];

export function TeamSortChip({
  sorting,
  onSortingChange,
}: Omittable<{
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}>) {
  return (
    <SortPresetChip presets={TEAM_SORT_PRESETS} sorting={sorting} onSortingChange={onSortingChange} />
  );
}
